import {action, makeObservable, reaction} from 'mobx';

import type {AudioModelFactory} from '../../../../../../core/models/Audio/AudioModel.factory';
import type {Nullable} from '../../../../../../utils/utility-types';

import {AudioPlayerState} from '../../../../../../components/AudioPlayer/state/AudioPlayerState';
import {FlagManager} from '../../../../../../utils/store';
import {AudioPlayerThemeManager} from './AudioPlayerThemeManager';

export class ArticleAudioPlayerState extends AudioPlayerState {
  isFullscreen = new FlagManager(false);
  themeManager = new AudioPlayerThemeManager();
  private onThemeChange: Nullable<ArticleAudioPlayerState.OnThemeChangeCb> =
    null;

  constructor(
    props: ArticleAudioPlayerState.Props,
    audioModelFactory: AudioModelFactory,
  ) {
    super(props, audioModelFactory);

    if (props.onThemeChange) {
      this.onThemeChange = props.onThemeChange;
    }

    this.themeManager.init(props.theme);
    makeObservable(this, {
      toggleFullscreen: action.bound,
    });
  }

  override async init(
    props: ArticleAudioPlayerState.InitialProps,
  ): Promise<void> {
    this.setupReactions();

    return super.init(props);
  }

  toggleFullscreen() {
    return this.isFullscreen.toggle();
  }

  private setupReactions() {
    this.disposer.watch(
      'onThemeChange',
      reaction(
        () => this.themeManager.activeId,
        themeKey => {
          this.onThemeChange?.(themeKey);
        },
      ),
    );
  }
}

export namespace ArticleAudioPlayerState {
  export type InitialProps = AudioPlayerState.InitialProps;

  export type OnThemeChangeCb = (
    themeKey: AudioPlayerThemeManager.ThemeKey,
  ) => void;

  export type Props = AudioPlayerState.Props & {
    onThemeChange?: OnThemeChangeCb;
    theme?: AudioPlayerThemeManager.ThemeKey;
  };
}
